/**
 * Schema-management default-value flows:
 * - `resetToSchemaDefaultsSummaryPreview` / `handleResetToSchemaDefaults` —
 *   reset the client config back to schema defaults with a summary preview.
 * - `updateToSchemaDefaultsSummaryPreview` / `handleUpdateSchemaDefaults` —
 *   pull in new schema-default values when the master schema has drifted.
 * - `updateSchemaWithNewDefaults` — dispatches the schema/master-schema
 *   updates after user confirmation.
 */

import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import { diff } from "deep-object-diff";
import { mergeAdvanced } from "object-merge-advanced";
import { getStore } from "@store";
import {
  isEmptyObject,
  removeEmptyValues,
  stringifyNestedKeyPaths,
} from "@utils/object-helpers";
import { arraysAreEqual } from "@utils/array-helpers";
import {
  generateBlankConfig,
} from "@features/sypi/_utils/sypi-interface-helpers";
import {
  logDiffs,
  schemaFormatter,
  convertPatternPropertiesSchemaToPropertiesSchema,
  getConfigWithUpdatedSchemaDefaults,
  generateArrayBlankConfig,
} from "@features/schema-management/utils/schemaManagementHelpers";
import { generateAthosConfig } from "../athosInterface";
import { CONFIG_SCHEDULER } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import {
  DISPATCH_ERRORS,
  ATHOS_DEFAULT_PATTERN_PROPERTY_KEY,
  SYPI_BRC,
} from "@constants/schema-management";
import {
  showSpinner,
  stopSpinner,
  setAlertDock,
  clearChanges,
  clearErrors,
  clearLog,
  clickTab,
  recordChangeObject,
  logChangedKey,
} from "@actions/global";
import {
  loadSchema,
  loadConfig,
  loadImportedInputs,
  loadMasterSchema,
  loadModifiedSchemas,
  loadNewDefaultsPreview,
  mergeDefaultConfig,
  setSchemaSectionSummaryName,
  toggleSectionsModal,
  toggleSummaryModal,
} from "@features/schema-management/actions";
import { getSummaryPreview } from "./sm-shared";
import { fetchSchema, fetchSMConfig } from "./sm-fetch";
import { setGeneralPreviewSummary } from "./sm-summary";
import { mergeAndHandleVisibilitySettings } from "./sm-errors";

/** reset to schema defaults */
export const resetToSchemaDefaultsSummaryPreview = async (
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const {
    schemaManagementConfig,
    defaultConfig,
    featureName,
    isAthosConfig,
    schemaType,
  } = store.getState().schemaAllView;
  const isSchemaArrayTyped = schemaType === "array";

  const updatedDefaultConfig = isAthosConfig
    ? Object.keys(schemaManagementConfig).reduce((acc, key) => {
        acc[key] = defaultConfig["clientName"];
        return acc;
      }, {})
    : defaultConfig;
  store.dispatch(
    clickTab(STATE_CONTANTS.DISPLAY_RESET_SUMMARY, CONFIG_SCHEDULER)
  );

  let changeDict =
    (isSchemaArrayTyped && !updatedDefaultConfig.length) ||
    isEmptyObject(updatedDefaultConfig)
      ? {}
      : removeEmptyValues(
          logDiffs(schemaManagementConfig, updatedDefaultConfig)
        );
  let loggedDiffs = Object.keys(changeDict);
  const { summaryPreview, successFailedImportedKeysReport } =
    await getSummaryPreview(
      isSchemaArrayTyped,
      schemaManagementConfig,
      loggedDiffs,
      updatedDefaultConfig
    );
  if (Object.keys(summaryPreview).length > 0) {
    await setGeneralPreviewSummary(
      featureName,
      summaryPreview,
      schemaManagementConfig,
      successFailedImportedKeysReport
    );
    store.dispatch(
      setSchemaSectionSummaryName(STATE_CONTANTS.DISPLAY_RESET_SUMMARY)
    );
    if (isAthosConfig) store.dispatch(toggleSectionsModal(true));
    store.dispatch(toggleSummaryModal(true));
  } else {
    store.dispatch(
      setAlertDock({
        title: "Client Config Contains All Schema Defaults",
        message: "No config changes detected.",
      })
    );
  }

  store.dispatch(stopSpinner());
};

export const handleResetToSchemaDefaults = async (store = getStore()) => {
  store.dispatch(showSpinner());
  const {
    schemaManagementConfig,
    defaultConfig,
    initialConfig,
    isAthosConfig,
    schemaType,
    featureName,
  } = store.getState().schemaAllView;
  const { generalPreview } = store.getState().schemaConfigScheduler;
  const { summaryView, summarySections, summarySectionsToUpdate } =
    generalPreview || [];
  const isSchemaArrayTyped = schemaType === "array";

  const updatedDefaultConfig = isAthosConfig
    ? Object.keys(schemaManagementConfig).reduce((acc, key) => {
        acc[key] = defaultConfig["clientName"];
        return acc;
      }, {})
    : defaultConfig;
  if (summaryView.length > 0) {
    let updatedImports = {};
    summaryView.forEach((key) => {
      updatedImports = { ...updatedImports, [key.keyName]: key.newKeyValue };
    });

    let mergedConfig = mergeAdvanced(
      schemaManagementConfig,
      updatedDefaultConfig,
      {
        concatInsteadOfMerging: false,
      }
    );

    if (!arraysAreEqual(summarySections, summarySectionsToUpdate)) {
      Object.keys(schemaManagementConfig).forEach((section) => {
        mergedConfig[section] = summarySectionsToUpdate.includes(section)
          ? mergedConfig[section]
          : schemaManagementConfig[section];
      });
    }

    let loggedDiffs = removeEmptyValues(
      logDiffs(initialConfig, mergedConfig, featureName)
    );
    store.dispatch(recordChangeObject(loggedDiffs));
    store.dispatch(logChangedKey(Object.keys(loggedDiffs)));
    store.dispatch(loadImportedInputs(updatedImports));
    store.dispatch(mergeDefaultConfig(mergedConfig, isSchemaArrayTyped));
    store.dispatch(
      setAlertDock({
        title: "Client Config Reset to Schema Defaults",
        message:
          "Schema default values have been loaded. Please schedule your changes to reset client config values to schema default values.",
      })
    );
  }
  store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  store.dispatch(stopSpinner());
};

/** update schema defaults */
export const updateToSchemaDefaultsSummaryPreview = async (
  scheduleNewDefaults = false,
  store = getStore()
) => {
  store.dispatch(showSpinner());

  const {
    initialConfig,
    featureName,
    errors,
    isAthosConfig,
    schemaType,
    changeDict,
  } = store.getState().schemaAllView;
  const {
    activeReleaseVersion,
    modifiedSchema,
    modifiedMasterSchema,
    clientSchema,
  } = store.getState().schema;

  const { formattedSchema, formattedMasterSchema } =
    await fetchSchema(activeReleaseVersion);
  if (!scheduleNewDefaults) {
    store.dispatch(
      clickTab(STATE_CONTANTS.DISPLAY_UPDATE_DEFAULTS_SUMMARY, CONFIG_SCHEDULER)
    );
  }
  const isSchemaArrayTyped = schemaType === "array";
  const clientConfig = await fetchSMConfig(activeReleaseVersion);
  let clientConfigTemplate = isAthosConfig
    ? generateAthosConfig(modifiedSchema)
    : isSchemaArrayTyped
      ? []
      : generateBlankConfig(formattedSchema);

  let masterConfigTemplate = isAthosConfig
    ? generateAthosConfig(modifiedMasterSchema)
    : isSchemaArrayTyped
      ? generateArrayBlankConfig(formattedMasterSchema)
      : generateBlankConfig(formattedMasterSchema);

  const smClientConfig =
    typeof clientConfig === "string"
      ? clientConfigTemplate
      : clientConfig.sm_config;

  const savedConfigChanges = diff(clientConfigTemplate, smClientConfig);
  const schemaHasChanged = diff(clientConfigTemplate, masterConfigTemplate);
  const hasErrors = errors && Object.keys(errors).some((key) => errors[key]);
  const schemaChanges = isAthosConfig
    ? diff(modifiedSchema, modifiedMasterSchema)
    : diff(clientSchema, formattedMasterSchema);
  const pathConfigChanges =
    savedConfigChanges && Object.entries(savedConfigChanges).length !== 0
      ? stringifyNestedKeyPaths(savedConfigChanges)
      : [];
  if (!isEmptyObject(schemaChanges) || hasErrors) {
    let updatedMasterConfigTemplate = cloneDeep(masterConfigTemplate);
    if (isSchemaArrayTyped) {
      updatedMasterConfigTemplate = smClientConfig.map((value) =>
        cloneDeep(masterConfigTemplate[0])
      );
      pathConfigChanges.forEach((path) => {
        const savedValue = get(savedConfigChanges, path);
        if (has(updatedMasterConfigTemplate, path))
          set(updatedMasterConfigTemplate, path, savedValue);
      });
    }

    let updatedConfig =
      pathConfigChanges &&
      pathConfigChanges.length &&
      (featureName === SYPI_BRC || isAthosConfig)
        ? getConfigWithUpdatedSchemaDefaults(
            pathConfigChanges,
            smClientConfig,
            updatedMasterConfigTemplate
          )
        : updatedMasterConfigTemplate;
    if (Object.entries(updatedConfig).length !== 0) {
      const diffMasterConfig = logDiffs(initialConfig, updatedConfig);
      let loggedDiffs = Object.keys(diffMasterConfig);
      const { summaryPreview, successFailedImportedKeysReport } =
        await getSummaryPreview(
          isSchemaArrayTyped,
          initialConfig,
          loggedDiffs,
          updatedConfig
        );
      const updatedSummeryPreview = Object.keys(summaryPreview).reduce(
        (acc, key) => {
          if (changeDict && !changeDict.hasOwnProperty(key)) {
            acc[key] = summaryPreview[key];
          }
          return acc;
        },
        {}
      );
      if (!Object.keys(updatedSummeryPreview).length) {
        store.dispatch(loadNewDefaultsPreview(activeReleaseVersion, []));
      }
      if (Object.keys(updatedSummeryPreview).length > 0) {
        await setGeneralPreviewSummary(
          featureName,
          updatedSummeryPreview,
          initialConfig,
          successFailedImportedKeysReport,
          scheduleNewDefaults,
          activeReleaseVersion
        );
        if (isAthosConfig) store.dispatch(toggleSectionsModal(true));
        store.dispatch(toggleSummaryModal(true));
      } else if (
        isEmptyObject(updatedSummeryPreview) &&
        Object.entries(schemaChanges).length !== 0
      ) {
        await updateSchemaWithNewDefaults(
          formattedMasterSchema,
          modifiedMasterSchema,
          isAthosConfig
        );
        store.dispatch(
          setAlertDock({
            title: "New Schema Configuration Defaults",
            message:
              "No config changes detected. Schema has been updated with new defaults.",
          })
        );
      } else {
        if (!scheduleNewDefaults)
          store.dispatch(
            setAlertDock({
              title: "New Schema Configuration Defaults",
              message: "No config changes detected.",
            })
          );
      }
      if (!isAthosConfig) {
        // Runs DISPATCH_ERRORS for its side effects; the returned schemaErrors
        // are captured only for parity with the source (unused here).
        // eslint-disable-next-line no-unused-vars
        const {
          fullErrorReport: { schemaErrors },
        } = DISPATCH_ERRORS[featureName](updatedConfig, formattedMasterSchema);
      }
    } else if (
      isEmptyObject(smClientConfig) &&
      Object.entries(schemaChanges).length !== 0
    ) {
      await updateSchemaWithNewDefaults(
        formattedMasterSchema,
        modifiedMasterSchema,
        isAthosConfig
      );
      store.dispatch(
        setAlertDock({
          title: "New Schema Configuration Defaults",
          message:
            "No config changes detected. Schema has been updated with new defaults.",
        })
      );
    } else {
      store.dispatch(
        setAlertDock({
          title: "New Schema Configuration Defaults",
          message: "No config or schemas changes detected.",
        })
      );
    }
  } else {
    if (!scheduleNewDefaults)
      store.dispatch(
        setAlertDock({
          title: "New Schema Configuration Defaults",
          message: "No config or schemas changes detected.",
        })
      );
  }
  store.dispatch(stopSpinner());
};

export const handleUpdateSchemaDefaults = async (store = getStore()) => {
  store.dispatch(showSpinner());
  const { initialConfig, featureName, isAthosConfig, schemaType } =
    store.getState().schemaAllView;
  const { activeReleaseVersion, modifiedSchema, modifiedMasterSchema } =
    store.getState().schema;
  const { generalPreview } = store.getState().schemaConfigScheduler;
  const { summaryView, summarySections, summarySectionsToUpdate } =
    generalPreview || [];
  const { formattedMasterSchema, formattedSchema, rawMasterSchema } =
    await fetchSchema(activeReleaseVersion);
  const isSchemaArrayTyped = schemaType === "array";
  const schemasDifference = isAthosConfig
    ? diff(modifiedSchema, modifiedMasterSchema)
    : diff(formattedSchema, formattedMasterSchema);
  const clientConfig = await fetchSMConfig(activeReleaseVersion);

  let clientConfigTemplate = isAthosConfig
    ? generateAthosConfig(modifiedSchema)
    : isSchemaArrayTyped
      ? []
      : generateBlankConfig(formattedSchema);

  let masterConfigTemplate = isAthosConfig
    ? generateAthosConfig(modifiedMasterSchema)
    : isSchemaArrayTyped
      ? generateArrayBlankConfig(formattedMasterSchema)
      : generateBlankConfig(formattedMasterSchema);

  const smClientConfig =
    typeof clientConfig === "string"
      ? clientConfigTemplate
      : clientConfig.sm_config;

  const savedConfigChanges = diff(clientConfigTemplate, smClientConfig);
  const pathConfigChanges =
    savedConfigChanges && Object.entries(savedConfigChanges).length !== 0
      ? stringifyNestedKeyPaths(savedConfigChanges)
      : [];
  const shouldUpdateInitial = typeof clientConfig === "string";
  const {
    fullErrorReport: { schemaErrors },
  } = DISPATCH_ERRORS[featureName](
    smClientConfig,
    formattedMasterSchema,
    "",
    false
  );
  store.dispatch(clearChanges());
  store.dispatch(clearErrors());
  store.dispatch(clearLog());
  if (summaryView.length > 0) {
    let updatedImports = {};
    summaryView.forEach((key) => {
      updatedImports = { ...updatedImports, [key.keyName]: key.newKeyValue };
    });
    store.dispatch(loadImportedInputs(updatedImports));
  }

  if (
    (schemasDifference && !isEmptyObject(schemasDifference)) ||
    (schemaErrors && schemaErrors?.length)
  ) {
    let updatedMasterConfigTemplate = cloneDeep(masterConfigTemplate);
    if (isSchemaArrayTyped) {
      updatedMasterConfigTemplate = smClientConfig.map((value) =>
        cloneDeep(masterConfigTemplate[0])
      );
      pathConfigChanges.forEach((path) => {
        const savedValue = get(savedConfigChanges, path);
        if (has(updatedMasterConfigTemplate, path))
          set(updatedMasterConfigTemplate, path, savedValue);
      });
    }
    let mergedConfig =
      pathConfigChanges &&
      pathConfigChanges.length &&
      (featureName === SYPI_BRC || isAthosConfig)
        ? getConfigWithUpdatedSchemaDefaults(
            pathConfigChanges,
            smClientConfig,
            updatedMasterConfigTemplate
          )
        : updatedMasterConfigTemplate;
    mergedConfig = mergeAndHandleVisibilitySettings(
      updatedMasterConfigTemplate,
      mergedConfig,
      false
    );
    if (!arraysAreEqual(summarySections, summarySectionsToUpdate)) {
      Object.keys(smClientConfig).forEach((section) => {
        mergedConfig[section] = summarySectionsToUpdate.includes(section)
          ? mergedConfig[section]
          : smClientConfig[section];
      });
    }
    let loggedDiffs = logDiffs(initialConfig, mergedConfig, featureName);
    store.dispatch(recordChangeObject(loggedDiffs));
    store.dispatch(logChangedKey(Object.keys(loggedDiffs)));
    store.dispatch(loadConfig(mergedConfig, shouldUpdateInitial));
    store.dispatch(loadMasterSchema(formattedMasterSchema, rawMasterSchema));
    await updateSchemaWithNewDefaults(
      formattedMasterSchema,
      modifiedMasterSchema,
      isAthosConfig
    );
    DISPATCH_ERRORS[featureName](mergedConfig, formattedMasterSchema);
  }
  store.dispatch(
    setAlertDock({
      title: "Update Client Config with New Schema Defaults",
      message:
        "New schema default values have been loaded. Please schedule your changes to update client config values to schema default values.",
    })
  );
  store.dispatch(clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER));
  store.dispatch(stopSpinner());
};

export const updateSchemaWithNewDefaults = async (
  formattedMasterSchema,
  modifiedMasterSchema,
  isAthosConfig = false,
  store = getStore()
) => {
  store.dispatch(loadSchema(formattedMasterSchema));
  if (isAthosConfig) {
    let updatedDefaultModifiedSchema = schemaFormatter(
      convertPatternPropertiesSchemaToPropertiesSchema(formattedMasterSchema, [
        ATHOS_DEFAULT_PATTERN_PROPERTY_KEY,
      ])
    );
    store.dispatch(
      loadModifiedSchemas(modifiedMasterSchema, updatedDefaultModifiedSchema)
    );
  }
};
